import { motion } from "framer-motion";
import { B, PILLARS, industryCallouts } from "../data/pillars";
import { getRiskLevel } from "../data/recommendations";
import Gauge from "./Gauge";
import MaturityRoadmap from "./MaturityRoadmap";
import SectionHeader from "./SectionHeader";

export default function ExecutiveSnapshot({
  intake, avgScore, risk, pillarScores, targetScore, months, answers, confidenceMap,
}) {
  return (
    <div style={{
      background: "#1A2E4A", borderRadius: 16, border: "1px solid #1E3A5F",
      padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box",
    }}>
      <SectionHeader num={1} title="Executive Summary" sub="High-Level Maturity Profile" />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 28, alignItems: "flex-start", marginBottom: 28 }}>
        {/* Gauge */}
        <div style={{ textAlign: "center", flexShrink: 0 }}>
          <Gauge score={avgScore} max={5} size={160} color={risk.color} />
          <div style={{
            background: risk.color, color: B.white, fontSize: 11, fontWeight: 800,
            padding: "4px 14px", borderRadius: 4, marginTop: 8, display: "inline-block",
          }}>{risk.label}</div>
        </div>

        {/* Narrative */}
        <div style={{ flex: "1 1 300px" }}>
          <p style={{ color: B.lightGray, fontSize: 15, lineHeight: 1.8, margin: "0 0 16px" }}>
            <strong style={{ color: B.white }}>{intake.company}</strong> is currently operating at{" "}
            <strong style={{ color: risk.color }}>OML {avgScore.toFixed(1)}</strong> —{" "}
            <strong style={{ color: risk.color }}>{risk.label.toLowerCase()}</strong>.{" "}
            {avgScore < 2
              ? "Critical gaps across security, operations, and infrastructure expose the business to immediate risk of breach, downtime, or compliance failure."
              : avgScore < 3
              ? "Technology gaps in key areas create meaningful operational and security risk that requires structured remediation."
              : "Core infrastructure is in place, but optimization opportunities exist to reduce cost, improve security posture, and enable strategic growth."}
          </p>

          {/* Strategic goal */}
          <div style={{
            background: "#122240", border: `1px solid ${B.blue}40`,
            borderRadius: 10, padding: "14px 18px", marginBottom: 16,
          }}>
            <div style={{ fontSize: 11, color: B.accent, fontWeight: 700, letterSpacing: 2, marginBottom: 8 }}>
              STRATEGIC GOAL
            </div>
            <p style={{ color: B.white, fontSize: 14, lineHeight: 1.6, margin: 0 }}>
              Move {intake.company} from OML {avgScore.toFixed(1)} to OML {targetScore.toFixed(1)} within{" "}
              {months} months — standardizing the security stack, modernizing infrastructure, and building
              an AI-ready managed technology foundation.
            </p>
          </div>

          {/* Quick stats row */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[
              { label: "Pillars Assessed", val: PILLARS.length, color: B.accent },
              { label: "Questions Answered", val: Object.keys(answers).length, color: B.green },
              { label: "Needs Validation", val: Object.values(confidenceMap).filter(v => v === false).length, color: B.yellow },
              { label: "Target OML", val: targetScore.toFixed(1), color: B.teal },
            ].map(s => (
              <div key={s.label} style={{
                background: "#0D1B2E", borderRadius: 8, padding: "10px 14px",
                flex: "1 1 80px", textAlign: "center", border: `1px solid ${s.color}20`,
              }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: 10, color: B.subText, fontWeight: 600, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Maturity Roadmap */}
      <MaturityRoadmap currentScore={avgScore} targetScore={targetScore} />

      {/* Industry callout */}
      {intake.vertical && industryCallouts[intake.vertical] && (
        <div style={{
          background: B.blue + "15", border: `1px solid ${B.blue}40`,
          borderRadius: 10, padding: "14px 18px", marginTop: 20,
        }}>
          <div style={{ fontSize: 11, color: B.accent, fontWeight: 700, letterSpacing: 2, marginBottom: 6 }}>
            ⚖️ {intake.vertical.toUpperCase()} COMPLIANCE CONTEXT
          </div>
          <p style={{ color: B.lightGray, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {industryCallouts[intake.vertical]}
          </p>
        </div>
      )}
    </div>
  );
}